import AppIconButton from "../form/AppIconButton";
import {
  Icon,
  Tooltip,
  useTheme,
  Box,
  ClickAwayListener,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import React, {
  useEffect,
  useState,
  useImperativeHandle,
  useCallback,
  useMemo,
} from "react";
import { AppTextField } from "../form";
import { getReportChatShell } from "../../lib/ui/reportChatShell";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";
import DataTable from "react-data-table-component";
import { getData } from "../../api/api";
import { showMessage } from "../../context";
import { useMaterialUIController } from "../../context";
import { getCurrentUserRoles, isViewer } from "../../utils/authorization";
import { useTranslation } from "react-i18next";
import { resolveDeleteItemName, useDeleteConfirm } from "../../hooks/useDeleteConfirm";

type RowAction = { key: string; label: string; icon: string; onClick: () => void };

/** Collapses a row's actions into one menu (tablet / phone) instead of a strip of small icons. */
function RowActionsMenu({ actions, label }: { actions: RowAction[]; label: string }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <AppIconButton aria-label={label} size="small" onClick={(e) => setAnchor(e.currentTarget)} busyIgnore>
        <Icon sx={{ color: "text.primary" }}>more_vert</Icon>
      </AppIconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {actions.map((a) => (
          <MenuItem
            key={a.key}
            onClick={() => {
              setAnchor(null);
              a.onClick();
            }}
          >
            <ListItemIcon>
              <Icon fontSize="small">{a.icon}</Icon>
            </ListItemIcon>
            <ListItemText>{a.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function PhonePager({ page, size, total, onChange }: { page: number; size: number; total: number; onChange: (p: number) => void }) {
  const { t } = useTranslation();
  if (total <= size) return null;
  const from = (page - 1) * size + 1;
  const to = Math.min(page * size, total);
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <AppIconButton aria-label={t("datatable.previousPage")} disabled={page <= 1} onClick={() => onChange(page - 1)} busyIgnore>
        <Icon>chevron_left</Icon>
      </AppIconButton>
      <Typography variant="body2">{t("datatable.range", { from, to, total })}</Typography>
      <AppIconButton aria-label={t("datatable.nextPage")} disabled={to >= total} onClick={() => onChange(page + 1)} busyIgnore>
        <Icon>chevron_right</Icon>
      </AppIconButton>
    </Stack>
  );
}

const DataTableWithPagination = React.forwardRef(({
  cols,
  handleEdit,
  handleDelete,
  handleDownload,
  fetchUrl,
  handleView,
  handleDraw,
  searchKey,
  searchPlaceholder,
  expandableRows,
  expandableRowsComponent,
  additionalParameters,
  handleSelect,
  /** Intent rules style: icon left, expand Outlined search on click, aligned with refresh */
  searchCollapseToIcon,
  deleteItemName,
}: any, ref) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const theme = useTheme();
  const custom = useAppCustomStyles();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { t } = useTranslation();
  const { confirmDelete, isCancelled } = useDeleteConfirm();
  const compact = useMediaQuery(theme.breakpoints.down("lg"));
  const phone = useMediaQuery(theme.breakpoints.down("sm"));

  const [searchValue, setSearchValue] = useState("");
  const [searchBarExpanded, setSearchBarExpanded] = useState(false);
  const searchFieldWidthPx = 280;
  const [errorMessage, setErrorMessage] = useState("");
  const [viewerRole, setViewerRole] = useState(false);

  useImperativeHandle(ref, () => ({
    refresh() {
      fetchData();
    },
  }));

  const handleRowsPerPageChange = (newRowsPerPage: any) => {
    setItemsPerPage(newRowsPerPage);
  };

  const deleteAction = async (item: any) => {
    try {
      const itemName = deleteItemName ? deleteItemName(item) : resolveDeleteItemName(item);
      await confirmDelete({ itemName });
      await handleDelete(item);
      fetchData();
    } catch (error) {
      if (isCancelled(error)) return;
      throw error;
    }
  };

  const viewAction = async (item: any) => {
    await handleView(item);
  };

  const drawAction = async (item: any) => {
    await handleDraw(item);
  };

  const downloadAction = async (item: any) => {
    await handleDownload(item);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    const queryStart = fetchUrl.includes("?") ? "&" : "?";
    const result = await getData(
      fetchUrl +
      queryStart +
      "page=" +
      (currentPage - 1) +
      "&size=" +
      itemsPerPage +
      "&sort=createdDate,desc" +
      ((searchValue || additionalParameters) ? "&search=" : "") +
      (additionalParameters ? additionalParameters : "") +
      (searchValue &&
        (additionalParameters ? ";(" : "") +
        searchKey +
        "=ilike=" +
        searchValue +
        ",description=ilike=" +
        searchValue +
        (additionalParameters ? ")" : "")
      ));
    setData(result);
    setLoading(false);
  }, [currentPage, fetchUrl, itemsPerPage, searchKey, searchValue, additionalParameters]);

  useEffect(() => {
    getCurrentUserRoles().then((roles) => setViewerRole(isViewer(roles)));
  }, []);

  useEffect(() => {
    fetchData();
  }, [currentPage, itemsPerPage, fetchData]);

  const currentItems = (data as any).content;

  const handlePageChange = (page: any) => setCurrentPage(page);
  const handleCopy = (data: any) => {
    navigator.clipboard.writeText(data.id);
    showMessage(t("datatable.copied"));
  };

  const rowActionList = (row: any): RowAction[] => {
    const list: RowAction[] = [];
    if (handleEdit) list.push({ key: "edit", label: t("common.edit"), icon: "edit", onClick: () => handleEdit(row) });
    if (handleView) list.push({ key: "view", label: t("datatable.view"), icon: "visibility", onClick: () => viewAction(row) });
    if (handleDraw) list.push({ key: "draw", label: t("datatable.draw"), icon: "draw", onClick: () => drawAction(row) });
    if (handleDownload) list.push({ key: "download", label: t("datatable.download"), icon: "download", onClick: () => downloadAction(row) });
    list.push({ key: "copy", label: t("datatable.copyId"), icon: "content_copy", onClick: () => handleCopy(row) });
    if (handleDelete && !viewerRole) list.push({ key: "delete", label: t("common.delete"), icon: "delete", onClick: () => deleteAction(row) });
    return list;
  };

  const actionSlotCount = [
    handleEdit,
    handleDelete && !viewerRole,
    handleView,
    handleDraw,
    handleDownload,
    true,
  ].filter(Boolean).length;

  const actionsColumnWidth = compact ? "64px" : `${Math.max(actionSlotCount, 3) * 40 + 20}px`;

  const columns = [
    {
      name: t("common.actions"),
      width: actionsColumnWidth,
      minWidth: actionsColumnWidth,
      maxWidth: actionsColumnWidth,
      style: custom.components.dataTable.actionsColumnStyle,
      cell: (row: any) => {
        if (compact) {
          return <RowActionsMenu actions={rowActionList(row)} label={t("datatable.rowActions")} />;
        }
        const actions = [];

        if (handleEdit) {
          actions.push(
            <AppIconButton
              key="edit"
              size="small"
              onClick={() => handleEdit(row)}
              sx={custom.components.dataTable.actionIcon}
            >
              <Icon sx={custom.components.dataTable.actionIconColor(darkMode)}>edit</Icon>
            </AppIconButton>
          );
        }

        if (handleDelete && !viewerRole) {
          actions.push(
            <AppIconButton
              key="delete"
              size="small"
              onClick={() => deleteAction(row)}
              sx={custom.components.dataTable.actionIcon}
            >
              <Icon sx={custom.components.dataTable.actionIconColor(darkMode)}>delete</Icon>
            </AppIconButton>
          );
        }

        if (handleView) {
          actions.push(
            <AppIconButton
              key="view"
              size="small"
              onClick={() => viewAction(row)}
              sx={custom.components.dataTable.actionIcon}
            >
              <Icon sx={custom.components.dataTable.actionIconColor(darkMode)}>visibility</Icon>
            </AppIconButton>
          );
        }

        if (handleDraw) {
          actions.push(
            <AppIconButton
              key="draw"
              size="small"
              onClick={() => drawAction(row)}
              sx={custom.components.dataTable.actionIcon}
            >
              <Icon sx={custom.components.dataTable.actionIconColor(darkMode)}>draw</Icon>
            </AppIconButton>
          );
        }

        if (handleDownload) {
          actions.push(
            <AppIconButton
              key="download"
              size="small"
              onClick={() => downloadAction(row)}
              sx={custom.components.dataTable.actionIcon}
            >
              <Icon sx={custom.components.dataTable.actionIconColor(darkMode)}>download</Icon>
            </AppIconButton>
          );
        }

        actions.push(
          <Tooltip key="copy" title={row.id}>
            <AppIconButton size="small" onClick={() => handleCopy(row)} sx={custom.components.dataTable.actionIcon}>
              <Icon sx={custom.components.dataTable.actionIconColor(darkMode)}>copy</Icon>
            </AppIconButton>
          </Tooltip>
        );

        return (
          <Box sx={custom.components.dataTable.actionsCell}>
            {actions}
          </Box>
        );
      },
      ignoreRowClick: true,
    },
    ...((cols ?? []) as any[]),
  ];

  const allowedPattern = /^[A-Za-z0-9*.,_-]*$/;

  const handleInputChange = (e: any) => {
    const value = e.target.value;
    if (allowedPattern.test(value)) {
      setSearchValue(value);
      setErrorMessage("");
    } else {
      setErrorMessage(t("datatable.searchInvalid"));
    }
  };
  const isSearchQueryEmpty = () => String(searchValue ?? "").trim() === "";

  const handleKeyDown = (event: any) => {
    if (event.key === "Enter") {
      fetchData();
    }
    if (searchCollapseToIcon && event.key === "Escape") {
      event.preventDefault();
      if (isSearchQueryEmpty()) {
        setSearchBarExpanded(false);
      }
    }
  };

  const searchTextFieldProps = {
    label: searchPlaceholder ?? searchKey,
    value: searchValue,
    onChange: (e: any) => handleInputChange(e),
    onKeyDown: handleKeyDown,
    variant: "outlined" as const,
    error: !!errorMessage,
    helperText: errorMessage,
    ...(searchCollapseToIcon
      ? {
          size: "small" as const,
          sx: custom.components.dataTable.searchFieldCollapsed,
        }
      : {}),
  };

  const chatShell = useMemo(() => getReportChatShell(theme), [theme]);
  const tableShellStyles = custom.components.dataTable.reportChatCustomStyles;

  const handleSearchClickAway = (event: MouseEvent | TouchEvent) => {
    if (!searchCollapseToIcon || !searchBarExpanded) return;
    const el = event.target;
    if (el instanceof Element && el.closest(".datatable-toolbar-refresh")) {
      return;
    }
    if (!isSearchQueryEmpty()) {
      return;
    }
    setSearchBarExpanded(false);
  };

  return (
    <Box sx={custom.components.dataTable.root(chatShell.appBg)}>
      <Box sx={[custom.components.dataTable.toolbarContainer, custom.components.dataTable.toolbarRow]}>
        {searchCollapseToIcon ? (
          <ClickAwayListener onClickAway={handleSearchClickAway}>
            <Box
              sx={[
                custom.components.dataTable.toolbarSearchRow(Boolean(searchBarExpanded && errorMessage)),
                custom.components.dataTable.searchCollapseWrapper,
              ]}
            >
              {!searchBarExpanded ? (
                <Tooltip title={t("datatable.search")} arrow>
                  <AppIconButton
                    aria-label={t("datatable.openSearch")}
                    size="medium"
                    onClick={() => setSearchBarExpanded(true)}
                    sx={custom.components.dataTable.searchIconButton}
                  >
                    <SearchRoundedIcon />
                  </AppIconButton>
                </Tooltip>
              ) : (
                <Box sx={custom.components.dataTable.searchExpandedBox}>
                  <AppTextField
                    {...searchTextFieldProps}
                    autoFocus
                    fullWidth
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <SearchRoundedIcon
                              fontSize="small"
                              sx={custom.components.dataTable.searchFieldAdornmentIcon}
                              aria-hidden
                            />
                          </InputAdornment>
                        ),
                      },
                      htmlInput: { tabIndex: 0 },
                    }}
                  />
                </Box>
              )}
            </Box>
          </ClickAwayListener>
        ) : (
          <Box>
            <AppTextField {...searchTextFieldProps} />
          </Box>
        )}
        <Box sx={custom.components.dataTable.refreshWrapper}>
          <Tooltip title={t("datatable.refresh")} arrow>
            <AppIconButton
              className="datatable-toolbar-refresh"
              size="medium"
              aria-label={t("datatable.refresh")}
              onClick={fetchData}
              sx={custom.components.dataTable.refreshButton}
            >
              <RefreshRoundedIcon />
            </AppIconButton>
          </Tooltip>
        </Box>
      </Box>
      {phone ? (
        <Box sx={{ p: 1 }}>
          {loading ? (
            <Stack alignItems="center" py={3}>
              <CircularProgress size={28} />
            </Stack>
          ) : !currentItems?.length ? (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
              {t("datatable.empty")}
            </Typography>
          ) : (
            <Stack spacing={1}>
              {currentItems.map((row: any, rowIndex: number) => {
                const shown = ((cols ?? []) as any[]).filter((col) => !col.omit);
                const [primary, ...rest] = shown;
                const render = (col: any) =>
                  col?.cell ? col.cell(row, rowIndex) : col?.selector ? String(col.selector(row) ?? "") : "";
                return (
                  <Paper
                    key={row.id ?? rowIndex}
                    variant="outlined"
                    sx={{ p: 1.5 }}
                    onClick={handleSelect ? () => handleSelect(row) : undefined}
                  >
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                      <Box sx={{ minWidth: 0, fontWeight: 600, overflowWrap: "anywhere" }}>{render(primary)}</Box>
                      <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0, mt: -0.5, mr: -0.5 }}>
                        <RowActionsMenu actions={rowActionList(row)} label={t("datatable.rowActions")} />
                      </Box>
                    </Stack>
                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                      {rest.map((col, i) => {
                        const value = render(col);
                        if (value === "" || value == null) return null;
                        return (
                          <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "baseline" }}>
                            <Typography variant="caption" color="text.secondary" sx={{ flex: "0 0 34%" }}>
                              {col.name}
                            </Typography>
                            <Box sx={{ minWidth: 0, flex: 1, fontSize: "0.875rem", overflowWrap: "anywhere" }}>{value}</Box>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Paper>
                );
              })}
              <PhonePager
                page={currentPage}
                size={itemsPerPage}
                total={(data as any)?.page?.totalElements || 0}
                onChange={handlePageChange}
              />
            </Stack>
          )}
        </Box>
      ) : (
      <DataTable
        columns={columns}
        data={currentItems}
        progressPending={loading}
        pagination={true}
        paginationServer={true}
        paginationTotalRows={(data as any)?.page?.totalElements || 0}
        onRowDoubleClicked={handleSelect}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handleRowsPerPageChange}
        paginationPerPage={(data as any)?.page?.size || 10}
        paginationRowsPerPageOptions={[50, 100, 200, 500]}
        highlightOnHover
        pointerOnHover
        customStyles={tableShellStyles}
        theme={darkMode ? "dark" : theme.palette.mode}
        expandableRows={expandableRows}
        expandableRowsComponent={expandableRowsComponent}
      />
      )}
    </Box>
  );
});

export default DataTableWithPagination;
